import {
  ModuleAccordionCards,
  ModuleAnimatedStats,
  ModuleBanner,
  ModuleBolloCalculator,
  ModuleCampaignBanner,
  ModuleCompetitorComparison,
  ModuleContentNavigation,
  ModuleDamageExplainer,
  ModuleDealershipPartnerHeader,
  ModuleTip,
  ModuleDeviceCarousel,
  ModuleGallery,
  ModuleGeneralVinSearch,
  ModuleHeroSection,
  ModuleHRContactArea,
  ModuleLandingPageVoucherEntry,
  ModuleNumberedSteps,
  ModulePackageWithoutVin,
  ModulePartnerLogos,
  ModuleReportFeatures,
  ModuleSampleVhr,
  ModuleScrollCards,
  ModuleStatCardCarousel,
  ModuleTextWithImage,
  ModuleUsps,
  PageModularModuleWrapper,
  ModuleFeatureIntro,
  ModulePictureHeading,
  ModuleFeaturesWithImage,
  ModuleFeaturesWithSampleReport,
  ModuleTestimonialModule,
  ModuleReportOverviewModule,
  ModuleDataSources,
  ModuleTextWithHighlightBoxes,
  ModulePricingSection,
  ModuleIconCards,
} from "@carfax/contentful/PageModularModule";
import { ModuleContentType } from "@carfax/contentful/types/content-type";
import { PricingPackage } from "domain/Pricing/PricingPage/Pricing.types";
import { useModuleLoaders } from "domain/CommonModules/moduleLoaders/ModuleLoadersContext";

interface ModularPageComponentWrapperProps {
  moduleWrapper: PageModularModuleWrapper;
  showSecondaryImage: boolean;
  indexPosition: number;
  packages?: PricingPackage[];
  minimumReportPrice?: number;
  businessPackages?: PricingPackage[];
}

const ModularPageComponentWrapper = ({
  moduleWrapper,
  showSecondaryImage,
  indexPosition,
  packages,
  minimumReportPrice,
  businessPackages,
}: ModularPageComponentWrapperProps) => {
  const moduleLoaders = useModuleLoaders();

  switch (moduleWrapper.contentType) {
    case ModuleContentType.ModuleSampleVhr:
      return (
        <moduleLoaders.SampleVhr
          module={moduleWrapper.module as ModuleSampleVhr}
          showSecondaryImage={showSecondaryImage}
        />
      );
    case ModuleContentType.ModuleReportFeatures:
      return <moduleLoaders.ReportFeatures module={moduleWrapper.module as ModuleReportFeatures} />;
    case ModuleContentType.ModuleBanner:
      return <moduleLoaders.PromotionBanner module={moduleWrapper.module as ModuleBanner} />;
    case ModuleContentType.ModulePartnerLogos:
      return <moduleLoaders.PartnerLogos module={moduleWrapper.module as ModulePartnerLogos} />;
    case ModuleContentType.ModuleTextWithImage:
      return (
        <moduleLoaders.TextImage indexPosition={indexPosition} module={moduleWrapper.module as ModuleTextWithImage} />
      );
    case ModuleContentType.ModuleGallery:
      return <moduleLoaders.Gallery module={moduleWrapper.module as ModuleGallery} />;
    case ModuleContentType.ModuleHRContactArea:
      return <moduleLoaders.HrContactArea module={moduleWrapper.module as ModuleHRContactArea} />;
    case ModuleContentType.ModuleUsps:
      return <moduleLoaders.Usps module={moduleWrapper.module as ModuleUsps} />;
    case ModuleContentType.ModuleAccordionCards:
      return <moduleLoaders.AccordionCards module={moduleWrapper.module as ModuleAccordionCards} />;
    case ModuleContentType.ModuleDeviceCarousel:
      return <moduleLoaders.DeviceCarousel module={moduleWrapper.module as ModuleDeviceCarousel} />;
    case ModuleContentType.ModuleCampaignBanner:
      return <moduleLoaders.CampaignBanner module={moduleWrapper.module as ModuleCampaignBanner} />;
    case ModuleContentType.ModulePackageWithoutVin:
      return <moduleLoaders.PackageWithoutVinModule module={moduleWrapper.module as ModulePackageWithoutVin} />;
    case ModuleContentType.ModuleGeneralVinSearch:
      return <moduleLoaders.GeneralVinSearch module={moduleWrapper.module as ModuleGeneralVinSearch} />;
    case ModuleContentType.ModuleLandingPageVoucherEntry:
      return <moduleLoaders.LandingPageVoucherEntry module={moduleWrapper.module as ModuleLandingPageVoucherEntry} />;
    case ModuleContentType.ModuleBolloCalculator:
      return <moduleLoaders.BolloCalculatorModule module={moduleWrapper.module as ModuleBolloCalculator} />;
    case ModuleContentType.ModuleCompetitorComparison:
      return <moduleLoaders.CompetitorComparison module={moduleWrapper.module as ModuleCompetitorComparison} />;
    case ModuleContentType.ModuleDamageExplainer:
      return <moduleLoaders.DamageExplainer module={moduleWrapper.module as ModuleDamageExplainer} />;
    case ModuleContentType.ModuleScrollCards:
      return <moduleLoaders.ScrollCards module={moduleWrapper.module as ModuleScrollCards} />;
    case ModuleContentType.ModuleHeroSection:
      return <moduleLoaders.HeroSection module={moduleWrapper.module as ModuleHeroSection} />;
    case ModuleContentType.ModuleStatCardCarousel:
      return <moduleLoaders.StatCardCarouselModule module={moduleWrapper.module as ModuleStatCardCarousel} />;
    case ModuleContentType.ModuleFeatureIntro:
      return <moduleLoaders.FeatureIntroModule module={moduleWrapper.module as ModuleFeatureIntro} />;
    case ModuleContentType.ModuleFeaturesWithImage:
      return <moduleLoaders.FeaturesWithImage module={moduleWrapper.module as ModuleFeaturesWithImage} />;
    case ModuleContentType.ModuleFeaturesWithSampleReport:
      return (
        <moduleLoaders.FeaturesWithSampleReportModule module={moduleWrapper.module as ModuleFeaturesWithSampleReport} />
      );
    case ModuleContentType.ModuleContentNavigation:
      return <moduleLoaders.ContentNavigationModule module={moduleWrapper.module as ModuleContentNavigation} />;
    case ModuleContentType.ModuleTip:
      return <moduleLoaders.TipModule module={moduleWrapper.module as ModuleTip} />;
    case ModuleContentType.ModuleAnimatedStats:
      return <moduleLoaders.AnimatedStats module={moduleWrapper.module as ModuleAnimatedStats} />;
    case ModuleContentType.ModuleTestimonialModule:
      return <moduleLoaders.TestimonialModule module={moduleWrapper.module as ModuleTestimonialModule} />;
    case ModuleContentType.ModuleReportOverviewModule:
      return <moduleLoaders.ReportOverviewModule module={moduleWrapper.module as ModuleReportOverviewModule} />;
    case ModuleContentType.ModuleSection:
      return null;
    case ModuleContentType.ModulePictureHeading:
      return <moduleLoaders.PictureHeadingModule module={moduleWrapper.module as ModulePictureHeading} />;
    case ModuleContentType.ModuleDataSources:
      return <moduleLoaders.DataSourcesSectionModule module={moduleWrapper.module as ModuleDataSources} />;
    case ModuleContentType.ModuleTextWithHighlightBoxes:
      return <moduleLoaders.TextWithHighlightBoxes module={moduleWrapper.module as ModuleTextWithHighlightBoxes} />;
    case ModuleContentType.ModulePricingSection:
      return <moduleLoaders.PricingSectionModule module={moduleWrapper.module as ModulePricingSection} />;
    case ModuleContentType.ModuleIconCards:
      return <moduleLoaders.ModuleIconCardsModule module={moduleWrapper.module as ModuleIconCards} />;
    case ModuleContentType.ModuleDealershipPartnerHeader:
      return (
        <moduleLoaders.DealershipPartnerHeaderModule
          module={moduleWrapper.module as ModuleDealershipPartnerHeader}
          packages={packages}
          minimumReportPrice={minimumReportPrice}
          businessPackages={businessPackages}
        />
      );
    case ModuleContentType.ModuleNumberedSteps:
      return <moduleLoaders.NumberedStepsModule module={moduleWrapper.module as ModuleNumberedSteps} />;
    default:
      return null;
  }
};

export default ModularPageComponentWrapper;
